// cameraEtc.js
import { COMTRUEVAR } from '../default/ocr_variables'
import { getPTTJPEG } from '../jpeg/pttjpeg-wrapper'

export const useCOMTRUECameraEtc = () => {
    const getCaptureCanvasSize = (cardType, video, captureCanvas) => {
        return new Promise((resolve, reject) => {
            const tempCanvas = document.createElement('canvas');

            let videoOffsetWidth = video.offsetWidth
            let videoOffsetHeight = video.offsetHeight
            let videoRatio = video.videoWidth / video.offsetWidth
            let frameYTop = 1 - COMTRUEVAR.BOX_CONTAINER_ROWS_TOP_RATIO
            let middleX = video.videoWidth / 2
            let middleY = video.videoHeight / (2 + frameYTop)
            let xyRatio = 1.58
            
            // [26.01.23] 신분증 가이드가 화면 상단 쪽에 있어 크롭 중심을 위로 이동
            const GUIDE_Y_RATIO = 0.38; 
            middleY = video.videoHeight * GUIDE_Y_RATIO;

            if (cardType) {
                switch (cardType) {
                    case 'passport':
                    case 'kpassport':
                        xyRatio = 1.18 
                        break
                    case 'imei':
                        xyRatio = 0.85
                        break
                    case 'usim_small':
                        xyRatio = 1.44
                        break
                    default:
                        //xyRatio = 1.32
                        xyRatio = 1.18
                        break
                }
            }

            let offsetX = (window.innerWidth / 2) * videoRatio + 200             
            let offsetY = offsetX / xyRatio

            if (offsetX * 2 >= video.videoWidth) {
                offsetX = video.videoWidth / 2 - 1
                offsetY = offsetX / xyRatio
            }
            
            if (offsetX >= middleX) offsetX = middleX
            if (offsetY >= middleY) offsetY = middleY

            let x1 = middleX - offsetX
            let y1 = middleY - offsetY
            let x2 = middleX + offsetX
            let y2 = middleY + offsetY

            let width = x2 - x1
            let height = y2 - y1

            let compressRatio = getCompressRatio(cardType)

            // [26.01.23] 1920x1080 해상도보다 작거나 같으면 배율 1.0으로 조정 (압축 해제)
            const longSide = Math.max(COMTRUEVAR.COM_WIDTH || 0, COMTRUEVAR.COM_HEIGHT || 0);
            const shortSide = Math.min(COMTRUEVAR.COM_WIDTH || 0, COMTRUEVAR.COM_HEIGHT || 0);

            if (longSide > 0 && longSide <= 1920 && shortSide <= 1080) {
                compressRatio = 1.0; 
                if (COMTRUEVAR.debugModeComtrue) {
                    console.log('[getCaptureCanvasSize] Low Resolution -> compressRatio = 1.0');
                }
            }

            // 계산된 캔버스 크기
            let canvasWidth = width * compressRatio;
            let canvasHeight = height * compressRatio;

            // 이미지 리사이즈 (Max Width Cap)
            const MAX_OUTPUT_WIDTH = 1300;
            if (canvasWidth > MAX_OUTPUT_WIDTH) {
                const scaleDownRatio = MAX_OUTPUT_WIDTH / canvasWidth;
                if (COMTRUEVAR.debugModeComtrue) {
                    console.log(`[Capture Resize] Too Big (${Math.round(canvasWidth)}px) -> Resizing to ${MAX_OUTPUT_WIDTH}px`);
                }
                canvasWidth = MAX_OUTPUT_WIDTH;
                canvasHeight = canvasHeight * scaleDownRatio;
            }

            // 임시 캔버스 크기 설정
            tempCanvas.width = canvasWidth
            tempCanvas.height = canvasHeight

            // 임시 캔버스에 그리기 (captureCanvas 아님)
            const ctx = tempCanvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) {
                reject(new Error("Failed to get 2D context from temp canvas"));
                return;
            }

            ctx.drawImage(
                    video,
                    x1,
                    y1,
                    width,
                    height,
                    0,
                    0,
                    canvasWidth,
                    canvasHeight
                )

            // jejeong [2025-06-10] 금보원 취약점 조치 :: 메모리 노출
            videoOffsetWidth = null
            videoOffsetHeight = null
            videoRatio = null
            frameYTop = null
            middleX = null
            middleY = null
            xyRatio = null
            offsetX = null
            offsetY = null
            x1 = null
            y1 = null
            x2 = null
            y2 = null
            width = null
            height = null
            compressRatio = null

            resolve(tempCanvas)
        })
            .then(function (processedCanvas) {
                // jejeong [2025-06-10] 메모리 영역 이미지 노출로 방식 변경
                // return getJpegBytes(captureCanvas);
                return getRgbaBytes(processedCanvas)
            })
            .catch(function (error) {
                console.log('getCaptureCanvasSize ERROR: ' + error)
                throw error
            })
    }

    const getRgbaBytes = (canvas) => {
        return new Promise(async (resolve, reject) => {
            let canvasWidth = canvas.width
            let canvasHeight = canvas.height
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
             if (!ctx) {
                reject(new Error("Failed to get 2D context for RGBA extraction"));
                return;
            }
            let imgData = ctx.getImageData(0, 0, canvasWidth, canvasHeight)
            
            let rgbArr = null

            rgbArr = await convertRgbArray(imgData)
            imgData = null

            resolve(rgbArr)
        })
            .then(function (rgbArr) {
                return rgbArr
            })
            .catch(function (error) {
                console.log('getRgbaBytes ERROR: ' + error)
                throw error
            })
    }

    /**
     * <pre>
     * imgData에서 rgbArray 추출 하는 함수
     * </pre>
     *
     * @param imgData
     * @returns {Promise<unknown>}
     * @since 2025.06.10
     */
    const convertRgbArray = (imgData) => {
        return new Promise(async (resolve, reject) => {
            let encoder = null
            let inImg = null
            let pttBw = null
            let rgbArr = []

            const pttJPEG = await getPTTJPEG()
            encoder = new pttJPEG()
            inImg = new encoder.pttImage(imgData)
            pttBw = new encoder.ByteWriter()

            // JPEG 퀄리티 설정 (기본 85, 저해상도 90)
            let quality = 85;
            
            const longSide = Math.max(COMTRUEVAR.COM_WIDTH || 0, COMTRUEVAR.COM_HEIGHT || 0);
            const shortSide = Math.min(COMTRUEVAR.COM_WIDTH || 0, COMTRUEVAR.COM_HEIGHT || 0);
            
            // 1920x1080 이하일 경우 퀄리티 상향
            if (longSide > 0 && longSide <= 1920 && shortSide <= 1080) {
                quality = 90;
            }

            encoder.encode(quality, inImg, pttBw)
            rgbArr = pttBw.getUnit8Array()

            encoder = null
            inImg = null
            pttBw = null

            resolve(rgbArr)
        })
            .then(function (rgbArr) {
                return rgbArr
            })
            .catch(function (error) {
                console.log('convertRgbArray ERROR: ' + error)
                throw error
            })
    }

    const getCompressRatio = (cardType) => {
        let compressRatio = 0.85
        if (cardType === 'imei') {
            compressRatio = 0.8
        }
        return compressRatio
    }

    const downloadImage = (data, filename = 'untitled.jpeg') => {
        if (COMTRUEVAR.debugModeComtrue) {
            console.log('[COMTRUECameraEtc.downloadImage] START')
        }
        const a = document.createElement('a')
        a.href = data
        a.download = filename
        document.body.appendChild(a)
        a.click()
    }

    return {
        getCaptureCanvasSize,
        getCompressRatio,
        downloadImage,
    }
}

export default useCOMTRUECameraEtc